import * as React from 'react'

import DesktopListTable from '~/client/src/desktop/tables/DesktopListTable'
import type { UseTableListResult } from '~/client/src/desktop/tables/useTableList'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ICompanyTypeTag from '~/client/src/shared/models/ICompanyTagType'
import User from '~/client/src/shared/models/User'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import getCompanyTypeTranslate from '~/client/src/shared/utils/getCompanyTypeTranslate'

import { IUserRow } from '../types'
import BandHeader from './BandHeader'

interface ITableProps {
  list: UseTableListResult<IUserRow>
  companiesStore: CompaniesStore
  onAddProjectMember: (companyId: string | null) => void
  onEditUser: (userId: string) => void
}

function getCompanyBandDisplay(
  companyId: string,
  companiesStore: CompaniesStore,
): {
  companyName: string
  companyIconUrl: string
  roleTagsLabel: string
} {
  const company = companyId ? companiesStore.getCompanyById(companyId) : null
  const typeTags = company
    ? companiesStore
        .getCompanyTypeTagsByIds(company.typeTags || [])
        .filter(Boolean)
    : []

  return {
    companyName: company?.name || User.getDefaultCompanyName(),
    companyIconUrl: company?.avatarUrl || company?.logoUrl || '',
    roleTagsLabel: typeTags
      .map((tag: ICompanyTypeTag) => getCompanyTypeTranslate(tag.value))
      .join(', '),
  }
}

export default function UserDirectoryTable({
  list,
  companiesStore,
  onAddProjectMember,
  onEditUser,
}: ITableProps): React.ReactElement {
  const renderBand = (
    band: import('~/client/src/desktop/tables/DesktopListTable').TableBandPresentation,
  ) => {
    const { companyName, companyIconUrl, roleTagsLabel } =
      getCompanyBandDisplay(band.optionId, companiesStore)

    return (
      <BandHeader
        companyId={band.optionId}
        companyName={companyName}
        companyIconUrl={companyIconUrl}
        roleTagsLabel={roleTagsLabel}
        memberCount={band.count}
        expanded={band.expanded}
        bandSelected={band.selected}
        bandIndeterminate={band.indeterminate}
        onToggleSelection={band.onToggleSelection}
        onToggleExpand={band.onToggleExpand}
        onAddProjectMember={onAddProjectMember}
      />
    )
  }

  return (
    <DesktopListTable
      list={list}
      ariaLabel={Localization.translator.userDirectory}
      renderBand={renderBand}
      onActivate={record => onEditUser(record.id)}
    />
  )
}
